import React from 'react';
import { Settings } from 'lucide-react';

// Import configuration
import { COMPLIANCE_FRAMEWORKS } from '../config';

/**
 * Infrastructure Automated Tests Component
 */
const InfrastructureAutomatedTests = ({ selectedRegions, awsRegions, setShowSettingsModal, isLoading, error }) => {
  // Get region names for the selected region IDs
  const getRegionNames = () => {
    return selectedRegions.map(regionId => {
      const region = awsRegions.find(r => r.id === regionId);
      return region ? region.name : regionId;
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">Infrastructure Automated Tests</h2>
      <p className="text-gray-700 mb-6">
        Our automated testing framework continuously verifies the implementation and effectiveness 
        of security controls across your cloud infrastructure.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-2">Test Coverage</h3>
          <p className="text-gray-700">
            Automated tests cover all security categories including log management, 
            vulnerability management, authentication, data protection, network security, 
            encryption, system hardening, intrusion detection, and infrastructure monitoring.
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex justify-between items-start">
            <h3 className="font-bold text-gray-800 mb-2">AWS Regions Covered</h3>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
            >
              <Settings size={14} className="mr-1" />
              Configure
            </button>
          </div>
          
          {isLoading ? (
            <p className="text-gray-500">Loading regions...</p>
          ) : error ? (
            <div className="text-red-600 text-sm">{error}</div>
          ) : selectedRegions.length === 0 ? (
            <p className="text-yellow-600">No regions selected. Click Configure to select regions.</p>
          ) : (
            <div>
              <p className="text-gray-700 mb-2">
                Security tests are run in the following AWS regions:
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedRegions.map(regionId => {
                  const region = awsRegions.find(r => r.id === regionId);
                  return (
                    <span 
                      key={regionId} 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800"
                      title={region ? region.name : ''}
                    >
                      {regionId}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-2">Test Frequency</h3>
          <p className="text-gray-700">
            Tests are run daily to ensure continuous compliance with security best practices.
            Critical tests are run hourly to provide early detection of security issues.
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-2">Compliance Frameworks</h3>
          <p className="text-gray-700">
            Our tests align with industry-standard compliance frameworks including {' '}
            {COMPLIANCE_FRAMEWORKS.map((framework, index) => (
              <React.Fragment key={framework}>
                {index > 0 && (index === COMPLIANCE_FRAMEWORKS.length - 1 ? ' and ' : ', ')}
                {framework}
              </React.Fragment>
            ))}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InfrastructureAutomatedTests;
